import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function isPdfFilename(filename) {
  return !!filename && filename.toLowerCase().endsWith('.pdf');
}

function AdminCertificateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [certificateNumber, setCertificateNumber] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [studentId, setStudentId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [existingFilePath, setExistingFilePath] = useState('');

  const [allStudents, setAllStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentCourses, setStudentCourses] = useState([]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchStudents = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/students`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setAllStudents(response.data.students || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchStudentCourses = useCallback(async (sId) => {
    if (!sId) { setStudentCourses([]); return; }
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/students/${sId}/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setStudentCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchCertificate = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/certificates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        const c = response.data.certificate;
        setCertificateNumber(c.certificate_number);
        setIssuedDate(c.issued_date ? c.issued_date.slice(0, 10) : '');
        setStudentId(c.student_id);
        setCourseId(c.course_id || '');
        setExistingFilePath(c.file_path);
        fetchStudentCourses(c.student_id);
      } else {
        setError(response.data.message || 'Failed to fetch certificate.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching certificate.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => { fetchCertificate(); }, [fetchCertificate]);

  const handleStudentChange = (newStudentId) => {
    setStudentId(newStudentId);
    setCourseId('');
    fetchStudentCourses(newStudentId);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setFilePreview(null);
    }
  };

  const filteredStudents = allStudents.filter((s) => {
    if (!studentSearch) return true;
    const q = studentSearch.trim().toLowerCase();
    return [s.firstName, s.lastName, s.studentid].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!certificateNumber.trim()) { setError('Certificate number is required.'); return; }
    if (!studentId) { setError('Please select a student.'); return; }
    if (!isEdit && !file) { setError('Please upload the certificate file.'); return; }

    setSaving(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('certificate_number', certificateNumber.trim());
      formData.append('student_id', studentId);
      formData.append('course_id', courseId || '');
      formData.append('issued_date', issuedDate || '');
      if (file) formData.append('file', file);

      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };
      const response = isEdit
        ? await axios.put(`${apiUrl}/api/admin/certificates/${id}`, formData, { headers })
        : await axios.post(`${apiUrl}/api/admin/certificates`, formData, { headers });

      if (response.data.success) {
        setSuccess(isEdit ? 'Certificate updated successfully.' : 'Certificate issued successfully.');
        if (!isEdit) setTimeout(() => navigate('/admin-certificates'), 800);
      } else {
        setError(response.data.message || 'Failed to save certificate.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving certificate.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-certificates" className="text-gray-200 fw-normal text-15 hover-text-main-600">Certificates</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{isEdit ? 'Edit Certificate' : 'Issue Certificate'}</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="row g-20">
                  <div className="col-12">
                    <label className="h5 mb-8 fw-semibold font-heading d-block">Student <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="form-control py-9 mb-8"
                      placeholder="Search student by name or student ID..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      disabled={saving}
                    />
                    <select
                      className="form-control py-11"
                      value={studentId}
                      onChange={(e) => handleStudentChange(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Select student...</option>
                      {filteredStudents.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.firstName} {s.lastName}{s.studentid ? ` (${s.studentid})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-sm-6">
                    <label className="h5 mb-8 fw-semibold font-heading">Course</label>
                    <select className="form-control py-11" value={courseId} onChange={(e) => setCourseId(e.target.value)} disabled={saving || !studentId}>
                      <option value="">{studentId ? 'Select course (optional)...' : 'Select a student first'}</option>
                      {studentCourses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="h5 mb-8 fw-semibold font-heading">Certificate Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="form-control py-11"
                      value={certificateNumber}
                      onChange={(e) => setCertificateNumber(e.target.value)}
                      disabled={saving}
                    />
                  </div>

                  <div className="col-sm-6">
                    <label className="h5 mb-8 fw-semibold font-heading">Issued Date</label>
                    <input type="date" className="form-control py-11" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} disabled={saving} />
                  </div>

                  <div className="col-12">
                    <label className="h5 mb-8 fw-semibold font-heading d-block">
                      Certificate File {!isEdit && <span className="text-red-500">*</span>}
                    </label>
                    <div className="d-flex align-items-center gap-16">
                      {filePreview ? (
                        <img src={filePreview} alt="preview" style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }} />
                      ) : file && isPdfFilename(file.name) ? (
                        <div style={{ width: 100, height: 80, borderRadius: 12, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db' }}>
                          <i className="ph ph-file-pdf text-gray-400" style={{ fontSize: 28 }} />
                        </div>
                      ) : existingFilePath ? (
                        isPdfFilename(existingFilePath) ? (
                          <a href={`${apiUrl}/${existingFilePath}`} target="_blank" rel="noreferrer" className="text-13 text-main-600 text-decoration-underline">Current: PDF file</a>
                        ) : (
                          <img src={`${apiUrl}/${existingFilePath}`} alt="current" style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }} />
                        )
                      ) : (
                        <div style={{ width: 100, height: 80, borderRadius: 12, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db' }}>
                          <i className="ph ph-certificate text-gray-400" style={{ fontSize: 28 }} />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control py-11"
                        accept="image/*,application/pdf"
                        onChange={handleFileChange}
                        disabled={saving}
                        style={{ maxWidth: 320 }}
                      />
                    </div>
                    {isEdit && <div className="text-13 text-gray-500 mt-8">Leave blank to keep the current file.</div>}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-8 mt-24">
                  <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={() => navigate('/admin-certificates')}>Back to List</button>
                  <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                    {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Issue Certificate'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminCertificateForm;
